<template>
  <div class="question-and-answer" :class="{ '-open': isOpen }">
    <div class="header text-md" @click="toggleOpen()">
      <p v-if="data.question" class="title text-md">
        {{ data.question }}
      </p>
      <button type="button" class="button">
        <img
          class="icon eye-closed"
          src="/icons/eye-closed.svg"
          alt="eye closed"
        />
        <img class="icon eye-open" src="/icons/eye-opened.svg" alt="eye open" />
      </button>
    </div>
    <div class="main">
      <StructuredText
        v-if="data.answer"
        class="structured-text content text-sm"
        :data="data.answer"
      />
      <div v-if="data.asset" class="asset">
        <VideoJSPlayer
          v-if="data.asset?.mimeType.includes('video') && data.asset?.video"
          class="video content"
          :class="{ 'video-ready': videoReady }"
          :options="{
            autoplay: false,
            muted: false,
            controls: true,
            loop: false,
            auto: true,
            fill: true,
            responsive: true,
            playsinline: true,
            hls: {
              limitRenditionByPlayerDimensions: true,
              useDevicePixelRatio: true,
            },
            bandwidth: 10e6,
            sources: [
              {
                src: data.asset?.video.streamingUrl,
                type: 'application/x-mpegURL',
              },
            ],
          }"
          @player-ready="onVideoReady"
        />
        <div v-if="data.asset?.mimeType.includes('image')" class="image">
          <img :src="data.asset.url" alt="" />
        </div>
        <DownloadItem
          v-if="data.asset.mimeType.includes('application/pdf')"
          :data="getPDF(data.asset)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Components
import { StructuredText } from 'vue-datocms';
import DownloadItem from '~/components/partials/DownloadItem.vue';
import VideoJSPlayer from '~/components/partials/VideoJSPlayer.vue';

// Types
import type { QuestionAndAnswerFragment, DownloadItemFragment } from '#gql';

// Props
defineProps<{
  data: QuestionAndAnswerFragment;
}>();

function getPDF(asset: QuestionAndAnswerFragment['asset']) {
  const item = {
    title: asset?.filename ?? 'PDF',
    document: asset,
  } as DownloadItemFragment;
  return item;
}

const isOpen = ref<boolean>(false);

function toggleOpen(open = !isOpen.value) {
  isOpen.value = open;
}

const videoReady = ref(false);

function onVideoReady() {
  videoReady.value = true;
}
</script>

<style scoped lang="scss">
.question-and-answer {
  width: 100%;
  position: relative;
  transition: padding-bottom 300ms ease-in-out;
  background: var(--base-color-white-80);
  border-radius: var(--base-border-radius-small);
  padding: 1.5rem 2rem;

  :deep(.icon) {
    width: 100%;
  }

  :deep(.icon.eye-open) {
    opacity: 0;
  }

  &.-open {
    > .main {
      grid-template-rows: 1fr;
      .content {
        margin-block: 1.5rem 0.5rem;
      }

      > .asset {
        height: fit-content;
        max-height: 1000px;
      }
    }

    :deep(.icon.eye-closed) {
      opacity: 0;
    }

    :deep(.icon.eye-open) {
      opacity: 1;
    }
  }

  > .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    cursor: pointer;

    .button {
      background: none;
      border: none;
      cursor: pointer;
      height: 1.5rem;
      width: 1.5rem;
      position: relative;
      padding: 0;

      .icon {
        position: absolute;
        top: 4px;
        transition: opacity 200ms ease-in-out;
      }
    }
  }

  > .main {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    transition: grid-template-rows 300ms ease-in-out;

    > .content {
      min-height: 0;
      max-width: 100%;
      display: grid;
      gap: 1.5rem;
      transition: margin-block 300ms ease-in-out;
    }
    > .asset {
      max-height: 0;
      transition: max-height 300ms ease-in-out;
    }
  }
}

@media (--vs) {
  .question-and-answer {
    padding: 1rem 0.75rem;
    background: var(--base-color-white-80);

    .header {
      display: grid;
      grid-template-columns: 1fr auto;
      .title {
        hyphens: auto;
      }
    }
  }
}
</style>
